import { VirtualFE87V2_MuYan, ZhiHuiWuXian_Device, type DefineDevice } from '../define/define'
import { deviceMap, deviceMode } from '../define/define_list'
import { fakeSocket } from '../fakesocket/fakesocket'
import { DriverSocketMsgType } from '@libs/types/message'
import { deviceProtocol } from './device_model'
import { DeviceWeb } from './device_web'
import { BaseDriver } from '../driver/device'
import { DeviceConnectMode, DeviceState } from '@libs/types/enum'
import { CurrentDevice, WatchDevice } from '@libs/types/response'
import { getDriverIns } from '@hid/driver/device_distance'
import { isWebHIDSupported } from '@libs/utils/envUtils'
import { filterGroupConflict, findDefineDeviceByHID, generateDeviceId, getDefineDevice, getFilterGroup, hidFilter } from '@hid/define/define_filters'
import { He6UltraDeviceList } from '@/define/device_vendor'
import { delay } from '@libs/utils/tools'

// export const deviceFitFilter = (dv: HIDDevice, filter: hidFilter): boolean => {
//   return dv.productId == filter.productId &&
//     dv.vendorId == filter.vendorId &&
//     dv.productName == filter.productName &&
//     dv.collections.some(c => c.usage && c.usage == filter.usage) &&
//     dv.collections.length == 1
// }
export const deviceFitFilter = (dv: HIDDevice, filter: hidFilter): boolean => {
  if (filter.productName) {
    const isNameMatch = dv.productName === filter.productName || dv.productName.trim() === filter.productName || dv.productName.includes(filter.productName)
    return dv.productId == filter.productId && dv.vendorId == filter.vendorId && isNameMatch && dv.collections.some((c) => c.usage && c.usage == filter.usage) && dv.collections.length == 1
  }

  return dv.productId == filter.productId && dv.vendorId == filter.vendorId && dv.collections.some((c) => c.usage && c.usage == filter.usage)
}
export interface UsingDeviceData {
  ID: number
  MaxDelay: number
  Name: string
}
export class DeviceManager {
  private currentDeviceData: UsingDeviceData | null = null
  private currentDeviceId: DefineDevice = 0x0c45c077 as DefineDevice //当前的连接设备
  private deviceModel: BaseDriver | null = null

  private onDisconnectResolve: (value: unknown) => void = () => {}
  private onDeviceChangeResolve: (value: unknown) => void = () => {}

  constructor() {
    this.onDeviceChange()
  }
  get deviceDriver() {
    if (this.deviceModel == null) {
      throw new Error('deviceModel is null')
    }
    return this.deviceModel
  }
  get deviceID() {
    return this.currentDeviceId
  }
  public webDevices = async (): Promise<HIDDevice[]> => {
    if (!isWebHIDSupported()) {
      throw new Error('当前浏览器不支持网页驱动功能，建议更换其他浏览器后再尝试')
    }
    let dvs = await navigator.hid.getDevices()
    if (dvs.length === 0) return []
    dvs = this.filterDevice(dvs)
    if (dvs.length === 0) return []
    return dvs
  }

  public forgetDevice = async () => {
    let dvs = await navigator.hid.getDevices()
    for (const device of dvs) {
      await device.forget()
    }
  }

  public getCurrentDevice() {
    return this.currentDeviceData
  }

  public onDeviceChange() {
    if (!isWebHIDSupported()) {
      return
    }
    navigator.hid.onconnect = async (event: HIDConnectionEvent) => {
      const collectDeviceID = getDefineDevice(event.device)
      if (ZhiHuiWuXian_Device.includes(collectDeviceID)) {
        await delay(20)
      }
      const dev = this.filterDevice([event.device])
      this.sendConnectionStatusMessage(dev, true)
    }

    navigator.hid.ondisconnect = (event: HIDConnectionEvent) => {
      const dev = this.filterDevice([event.device])
      this.onDisconnectNotify(dev)
      this.deviceModel?.destroy() // 显式销毁旧驱动实例，如清除定时器
      this.sendConnectionStatusMessage(dev, false)
      deviceProtocol.disconnect()
    }
  }
  private onDisconnectNotify(dev: HIDDevice[]) {
    if (dev.length > 0) {
      const deviceID = getDefineDevice(dev[0])
      if (this.deviceID == deviceID) {
        this.onDisconnectResolve(true)
      }
    }
  }

  private sendConnectionStatusMessage(hid: HIDDevice[], In: boolean) {
    if (hid.length == 0) {
      // console.log('device not found')
      return
    }
    const data: WatchDevice = { Device: getDefineDevice(hid[0]), In: In, State: DeviceState.StateUnlink }
    fakeSocket.sendMsgData(DriverSocketMsgType.MSWatchDevice, data)
  }

  async subscriberOnDisconnect() {
    return new Promise((resolve) => {
      this.onDisconnectResolve = resolve
    })
  }

  async subscriberOnDeviceChange() {
    return new Promise((resolve) => {
      this.onDeviceChangeResolve = resolve
    })
  }

  // private testMonsGeek(deviceID:DefineDevice) {
  //   if ((deviceID == VirtualMonsGeek ||
  //       deviceID == VirtualMonsGeek_NoLight ||
  //       deviceID == VirtualMonsGeek_NoLight_24g)
  //       && import.meta.env.DEV) {
  //     deviceID = VirtualMars68ProWireless
  //   }
  // }

  async connect(deviceID: DefineDevice, _node: boolean = false): Promise<boolean> {
    if (deviceID != this.deviceID) {
      this.onDeviceChangeResolve(true)
    }
    const webList = await this.webDevices()
    const useCurrent = webList.find((dv) => this.findConnectWebDevices(dv, deviceID) === deviceID)
    if (useCurrent == undefined) {
      throw new Error('device not found')
    }

    // this.testMonsGeek(deviceID)

    this.currentDeviceId = deviceID

    if (this.deviceModel) {
      this.deviceModel.destroy() // 切换设备前销毁旧实例
    }
    await deviceProtocol.setBase(new DeviceWeb(useCurrent))
    this.deviceModel = await getDriverIns(deviceID, deviceProtocol)

    this.currentDeviceData = {
      ID: deviceID,
      MaxDelay: 0,
      Name: useCurrent.productName,
    }
    return true
  }

  public request = async (): Promise<HIDDevice[]> => {
    if (!isWebHIDSupported()) {
      throw new Error('当前浏览器不支持网页驱动功能，建议更换其他浏览器后再尝试')
    }
    const devices = await navigator.hid.requestDevice({
      filters: getFilterGroup(),
    })
    console.log('debug:', devices)
    return devices
  }

  private isExceptionCollections(vid: number, pid: number) {
    const device = (vid << 16) | pid
    if (He6UltraDeviceList.includes(device)) {
      return true
    }
    if (VirtualFE87V2_MuYan == device) {
      return true
    }
    return false
  }

  private filterDevice(devices: HIDDevice[]) {
    const filter_group = getFilterGroup()
    const filterUsages = filter_group.map((f) => f.usage)
    const filterUsagePage = filter_group.map((f) => f.usagePage)
    let dvs = devices.filter(
      (dv) => dv.collections.some((collection) => filterUsages.includes(collection.usage || 0) && filterUsagePage.includes(collection.usagePage || 0)) && (dv.collections.length == 1 || this.isExceptionCollections(dv.vendorId, dv.productId)),
    )

    //如果有设备同时存在有线和无线状态, 只取用有线状态
    filterGroupConflict.forEach((i) => {
      const f1 = dvs.some((dv) => deviceFitFilter(dv, i[0]))
      const f2 = dvs.some((dv) => deviceFitFilter(dv, i[1]))
      if (f1 && f2) dvs = dvs.filter((dv) => !deviceFitFilter(dv, i[1]))
    })
    return dvs
  }

  public respOfDevices = (devices: HIDDevice[]) => {
    const devs: CurrentDevice[] = []
    devices.forEach((e) => {
      const devID = findDefineDeviceByHID(e)
      if (devID == undefined || !deviceMap[devID]) {
        return
      }
      const devData = deviceMap[devID].support
      devs.push({
        ID: devID,
        IsSupportMagnet: devData.IsSupportMagnet,
        IDStr: '',
        ProductName: e.productName,
        IsEEvision: false,
        ConnectMode: this.getDeviceMode(devID),
        Limit: devData.Limit,
      })
    })
    return devs
  }

  // private getDeviceMode(collections: HIDCollectionInfo[]): DeviceConnectMode {
  //   for (const collection of collections) {
  //     if (usageToDeviceMode[collection.usage || 0] !== undefined) {
  //       return usageToDeviceMode[collection.usage || 0];
  //     }
  //   }
  //   return DeviceConnectMode.Normal; // 使用默认模式，如果没有匹配的 usage
  // }
  private getDeviceMode(devID: DefineDevice): DeviceConnectMode {
    const connectMode = deviceMode[devID]
    if (connectMode) {
      return connectMode
    }
    return DeviceConnectMode.Normal // 使用默认模式,如果没有找到匹配的设备模式
  }

  // private getDeviceID(device: hidFilter, e: HIDDevice): DefineDevice {
  //   if (device.secondaryProductId == undefined) {
  //     return (e.vendorId * 0x10000 + e.productId) as DefineDevice
  //   }
  //   const secondaryId = device.secondaryProductId || 0
  //   const deviceID = (device.vendorId * 0x10000 + device.productId) as DefineDevice
  //   return (secondaryId * 0x100000000 + deviceID) as DefineDevice
  // }

  // private getDefineDevice(e: HIDDevice): DefineDevice {
  //   const filter_hid = getFilterGroup().filter((f) =>
  //      (f.vendorId == e.vendorId || f.vendorId_new == e.vendorId) &&
  //      (f.productId == e.productId || f.productId_new == e.productId) &&
  //      f.productName == e.productName)

  //   if (filter_hid.length == 0) {
  //     return (e.vendorId * 0x10000 + e.productId) as DefineDevice
  //   }
  //   return this.getDeviceID(filter_hid[0], e)
  // }

  private findConnectWebDevices(e: HIDDevice, connectDeviceID: DefineDevice): DefineDevice | null {
    const filters = getFilterGroup().filter(
      (f) =>
        (f.vendorId == e.vendorId || f.vendorId_new == e.vendorId) && (f.productId == e.productId || f.productId_new == e.productId) && (e.productName === f.productName || e.productName.trim() === f.productName || e.productName.includes(f.productName)),
    )
    // 选取 productName 最长的匹配项，以避免前缀匹配问题（如 foo vs foo Pro）
    filters.sort((a, b) => b.productName.length - a.productName.length)

    const devID = filters.find((device) => generateDeviceId(device, e) == connectDeviceID)
    if (devID) {
      return connectDeviceID
    }
    return null
  }
}

export const deviceManager = new DeviceManager()
